import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Globe,
  MapPin,
  Trophy,
  Users,
  Target,
  ChevronRight,
  Crown,
  Calendar
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const TourDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { member } = useAuth();
  const [tour, setTour] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTourDetail();
  }, [id]);

  const fetchTourDetail = async () => {
    try {
      const response = await axios.get(`${API_URL}/tours/${id}`);
      setTour(response.data.tour);
      setTournaments(response.data.tournaments);
    } catch (error) {
      toast.error('Failed to load tour');
      navigate('/tours');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'status-open',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      closed: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500',
      archived: 'bg-gray-500/20 text-gray-500 border border-gray-500'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      completed: 'Completed',
      closed: 'Closed',
      archived: 'Archived'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.completed}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!tour) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/tours')}
              className="text-muted-foreground hover:text-white"
              data-testid="back-to-tours-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Tours
            </Button>
          </div>
          <img
            src={TGA_LOGO}
            alt="TGA Pro Tour"
            className="h-10 object-contain"
          />
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Tour Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-8 h-8 text-primary" />
            <h1 className="text-3xl sm:text-4xl font-heading text-white" data-testid="tour-detail-name">
              {tour.name}
            </h1>
          </div>
          {tour.region && (
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <MapPin className="w-4 h-4" />
              <span>{tour.region}</span>
            </div>
          )}
          {tour.description && (
            <p className="text-muted-foreground mt-2">{tour.description}</p>
          )}
          <div className="mt-4 flex items-center gap-2 text-sm text-primary font-mono">
            <Trophy className="w-4 h-4" />
            {tournaments.length} Tournament{tournaments.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Tournaments in this Tour */}
        {tournaments.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="p-12 text-center">
              <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading text-white mb-2">No Tournaments Yet</h3>
              <p className="text-muted-foreground">
                No tournaments have been assigned to this tour yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {tournaments.map((tournament, index) => (
              <Card
                key={tournament.id}
                className="bg-card border-white/10 tournament-card cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/tournaments/${tournament.id}`)}
                data-testid={`tour-tournament-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-xl font-heading text-white">
                          {tournament.name}
                        </h3>
                        {getStatusBadge(tournament.status)}
                        {tournament.tournament_type === 'pro' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/50">
                            <Crown className="w-3 h-3" />
                            Pro Tour
                          </span>
                        )}
                      </div>
                      {tournament.description && (
                        <p className="text-muted-foreground text-sm mb-3">
                          {tournament.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Target className="w-4 h-4" />
                          {tournament.num_holes} Holes
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {tournament.participants_count} Players
                        </span>
                        {tournament.event_date && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {formatDate(tournament.event_date)}
                          </span>
                        )}
                        {tournament.venue_name && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            {tournament.venue_name}
                          </span>
                        )}
                        <span className="text-primary font-mono">
                          Par {tournament.total_par}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 text-muted-foreground hidden md:block" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
